/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, ReactElement } from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { isMentionAvatarEnabled } from '@atlaskit/editor-common/mention-avatar';
import type { Mention as OriginalMention } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

// Match Platform PrimitiveMention's font-relative geometry. Compiled permits its
// precise em padding and relative line-height, which @atlaskit/css does not support.
const styles = cssMap({
	mention: {
		display: 'inline',
		backgroundColor: token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape -- Matches Platform PrimitiveMention's 20px pill geometry.
		borderRadius: '20px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		color: token('color.text.subtle'),
		paddingBlockStart: 0,
		paddingBlockEnd: token('space.025'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Matches Platform PrimitiveMention's font-relative padding.
		paddingInlineStart: '0.23em',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Matches Platform PrimitiveMention's font-relative padding.
		paddingInlineEnd: '0.3em',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches Platform PrimitiveMention's inherited-font line box.
		lineHeight: 1.714,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Inherits the surrounding renderer typography.
		fontSize: '1em',
		fontWeight: token('font.weight.regular'),
		wordBreak: 'break-word',
	},
	withAvatar: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Matches Platform's one-pixel avatar alignment.
		paddingBlockStart: '1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Matches Platform's one-pixel avatar alignment.
		paddingBlockEnd: '1px',
	},
	avatar: {
		display: 'inline-flex',
		width: '1em',
		height: '1em',
		marginInlineEnd: token('space.050'),
		verticalAlign: '-0.125em',
	},
});

/** Reserve Platform's avatar geometry without loading images or subscribing to providers. */
export const Mention = ({
	id,
	text,
	localId,
	userType,
	mentionNodeDataProvider,
	lightNodeRef,
}: ComponentProps<typeof OriginalMention> & LightNodeProps): ReactElement => {
	if (isMentionAvatarEnabled({ id, userType, hasProvider: Boolean(mentionNodeDataProvider) })) {
		return (
			<span
				ref={lightNodeRef}
				css={[styles.mention, styles.withAvatar]}
				data-mention-id={id}
				data-local-id={localId}
			>
				<span aria-hidden="true" css={styles.avatar} data-testid="light-mention-avatar-slot" />
				<span>{text.startsWith('@') ? text.slice(1) : text}</span>
			</span>
		);
	}
	return (
		<span ref={lightNodeRef} css={styles.mention} data-mention-id={id} data-local-id={localId}>
			{text || '@...'}
		</span>
	);
};
